import {t} from './localeText';
import {useEffect, useMemo, useState} from 'react';
import s from './index.module.less';

const ProjectCatalog = ({projects, onOpen, lang = 'zh'}) => {
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('');
    const searchedProjects = useMemo(() => projects.filter(project =>
        `${project.title} ${project.desc} ${project.keywords} ${project.category}`.toLowerCase().includes(query.trim().toLowerCase())
    ), [projects, query]);
    const categories = useMemo(() => [
        {name: t(lang, '全部项目'), value: '', count: searchedProjects.length},
        ...[...new Set(projects.map(project => project.category))].map(name => ({
            name,
            value: name,
            count: searchedProjects.filter(project => project.category === name).length,
        })),
    ], [projects, searchedProjects, lang]);
    const visible = useMemo(() => searchedProjects.filter(project =>
        !category || project.category === category
    ), [searchedProjects, category]);

    useEffect(() => {
        const reveal = () => {
            const hash = window.location.hash.slice(1);
            const project = projects.find(item => hash === `project-${item.id}` || (Number.isInteger(item.legacyIndex) && hash === `resume-project-${item.legacyIndex}`));
            if (!project) return;
            setQuery('');
            setCategory('');
            onOpen({type: 'project', key: project.id, payload: project});
        };
        const repeatAnchor = event => {
            const anchor = event.target.closest('a[href^="#"]');
            if (anchor && anchor.getAttribute('href') === window.location.hash) reveal();
        };
        reveal();
        window.addEventListener('hashchange', reveal);
        document.addEventListener('click', repeatAnchor);
        return () => {
            window.removeEventListener('hashchange', reveal);
            document.removeEventListener('click', repeatAnchor);
        };
    }, [projects, onOpen]);

    const open = (event, project) => {
        event.currentTarget.focus({preventScroll: true});
        onOpen({type: 'project', key: project.id, payload: project});
    };

    return <section className={s.projectCatalog} id='project-catalog' aria-labelledby='project-catalog-title'>
        <header className={s.catalogHead}>
            <div><span>PROJECTS / PRACTICE</span><h2 id='project-catalog-title'>{t(lang, '项目与实践')}</h2><p>{t(lang, '从技术判断到系统实现，再到业务使用与持续演进。')}</p></div>
            <a href='#capability-model'>{t(lang, '回看能力全景')} ↗</a>
        </header>
        <div className={s.catalogControls}>
            <p className={s.catalogCategoryLabel} id='project-category-label'>{t(lang, '项目分类')}</p>
            <div className={s.catalogCategories} role='group' aria-labelledby='project-category-label'>
                {categories.map(option => <button
                    key={option.value}
                    type='button'
                    aria-pressed={category === option.value}
                    aria-label={`${option.name}, ${option.count} ${t(lang, '个项目')}`}
                    onClick={() => setCategory(option.value)}
                ><span>{option.name}</span><span aria-hidden='true'>{option.count}</span></button>)}
            </div>
            <div className={s.catalogSearch}>
                <label><span>{t(lang, '检索项目')}</span><input type='search' value={query} onChange={event => setQuery(event.target.value)} placeholder={t(lang, '名称、方向或关键词…')}/></label>
            </div>
        </div>
        <p className={s.catalogStatus} role='status'>{visible.length} {t(lang, '项匹配')}</p>
        <div className={s.catalogGrid}>
            {visible.map(project => <article className={s.catalogCard} key={project.id} id={`project-${project.id}`}>
                {Number.isInteger(project.legacyIndex) ? <span className={s.catalogAnchor} id={`resume-project-${project.legacyIndex}`}/> : null}
                <div className={s.catalogCardMeta}><span>{project.category}</span><em>{project.stage}</em></div>
                <h3><button type='button' className={s.summaryCardTrigger} aria-haspopup='dialog' onClick={event => open(event, project)}>{project.title}</button></h3>
                <p className={s.catalogCardDescription}>{project.desc}</p>
                {project.metric ? <p className={s.catalogCardResult}>{project.metric}</p> : null}
                <footer><span>{t(lang, project.architectureKey ? '详情与架构' : '查看详情')} <i aria-hidden='true'>↗</i></span></footer>
            </article>)}
        </div>
        {!visible.length ? <div className={s.catalogEmpty}><p>{t(lang, '没有匹配的项目')}</p><button type='button' onClick={() => {setQuery(''); setCategory('');}}>{t(lang, '清除筛选')}</button></div> : null}
    </section>;
};

export default ProjectCatalog;
